import { Button, Grid } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import React from 'react';

import { ModelSnackBarProgressBarItem } from '~/src/dash/components/model-snackbar-progresbar-item';
import { decorate } from '~/src/dash/utils/decorator';
import { smallRadius } from '~/src/dash/utils/material-dashboard-react';
import UploadManager, { EUploadResponseType, IUploadStatus } from '~/src/dash/utils/upload_manager';

export interface IModelSnackBarProgressBarContainerProps {
  uploadManager: UploadManager;
  isMediafile?: boolean;
  isSavable?: boolean;
}

interface IState {
  currentUploads: IUploadStatus[];
  isMinimized: boolean;
  isClosed: boolean;
}

export const ModelSnackBarProgressBarContainer = decorate(
  {
    styles: () => ({}),
  },
  class extends React.Component<IModelSnackBarProgressBarContainerProps, IState> {
    state = {
      currentUploads: [] as IUploadStatus[],
      isMinimized: false,
      isClosed: true,
    };

    componentDidMount(): void {
      this.invertMinimizeState = this.invertMinimizeState.bind(this);
      this.closeSnackbar = this.closeSnackbar.bind(this);

      this.props.uploadManager.events.addListener(
        'uploadManager_uploadStatusChange',
        (result: IUploadStatus) => {
          if (result.status === EUploadResponseType.DELETED) {
            this.setState({
              currentUploads: this.state.currentUploads.filter((value: IUploadStatus) => {
                return value.key !== result.key;
              }),
            });
            return;
          }

          if (result.status === EUploadResponseType.ADDED) {
            const currentUploads = [...this.state.currentUploads];
            currentUploads.push(result);
            this.setState({
              isMinimized: false,
              isClosed: false,
              currentUploads,
            });
            return;
          }

          const index = this.state.currentUploads.findIndex((item: IUploadStatus) => {
            return item.key === result.key;
          });

          // update the current uploads
          if (index !== -1) {
            this.setState((prevState: IState) => {
              prevState.currentUploads[index].status = result.status;
              return {
                currentUploads: prevState.currentUploads,
              };
            });
          }
        },
      );

      // event listener for adding multiple files
      this.props.uploadManager.events.addListener(
        'uploadManager_uploadStatusChange_multi',
        (results: IUploadStatus[]) => {
          if (results.length === 0) {
            return;
          }

          // check to see if the listener is sending a deleted msg
          // the results should all either be deleted or added
          if (results[0].status === EUploadResponseType.DELETED) {
            const deleteKeys = [] as string[];
            for (const result of results) {
              deleteKeys.push(result.key);
            }
            this.setState({
              currentUploads: this.state.currentUploads.filter((value: IUploadStatus) => {
                return !deleteKeys.includes(value.key);
              }),
            });
            return;
          }

          // check to see if the listener is sending an added msg
          // the results should all either be deleted or added
          if (results[0].status === EUploadResponseType.ADDED) {
            const currentUploads = [...this.state.currentUploads];
            for (const result of results) {
              currentUploads.push(result);
            }
            this.setState({
              isMinimized: false,
              isClosed: false,
              currentUploads,
            });
            return;
          }

          // update the current uploads
          const currentUploads = [...this.state.currentUploads];
          for (const result of results) {
            const index = currentUploads.findIndex((item: IUploadStatus) => {
              return item.key === result.key;
            });
            if (index >= 0) {
              currentUploads[index].status = result.status;
            }
          }
          this.setState({
            currentUploads,
          });
        },
      );
    }

    isThereUploadsInProgress(uploads: IUploadStatus[]) {
      for (const up of uploads) {
        if (up.status === EUploadResponseType.PROGRESS || up.status === EUploadResponseType.ADDED) {
          return true;
        }
      }
      return false;
    }

    invertMinimizeState() {
      this.setState({
        isMinimized: !this.state.isMinimized,
      });
    }

    render() {
      if (this.state.isClosed || this.state.currentUploads.length === 0) {
        return <div></div>;
      }

      const buttonExpand = this.state.isMinimized ? (
        <Button onClick={this.invertMinimizeState}>
          <ExpandLessIcon />{' '}
        </Button>
      ) : (
        <Button onClick={this.invertMinimizeState}>
          <ExpandMoreIcon />{' '}
        </Button>
      );

      const showCloseButton = !this.isThereUploadsInProgress(this.state.currentUploads);

      return (
        <div
          style={{
            width: '400px',
            height: 'auto',
            borderRadius: smallRadius,
            backgroundColor: 'white',
            boxShadow: ' 0px 3px 6px #CBCBCB',
            lineHeight: '2px',
          }}
        >
          <div
            style={{
              borderRadius: '5px 5px 0px 0px',
              backgroundColor: 'lightgray',
              height: 'auto',
              flexGrow: 1,
              margin: '0px',
            }}
          >
            <Grid container direction='row' justify='flex-start' alignItems='center'>
              <Grid
                item
                xs={8}
                style={{
                  paddingLeft: '20px',
                }}
              >
                Current Upload ({this.state.currentUploads.length})
              </Grid>
              {!showCloseButton && <Grid item xs />}
              <Grid item xs>
                {buttonExpand}
              </Grid>
              {showCloseButton && (
                <Grid item xs>
                  <Button onClick={this.closeSnackbar}>
                    <CloseIcon />
                  </Button>
                </Grid>
              )}
            </Grid>
          </div>
          {!this.state.isMinimized && (
            <div
              style={{
                borderRadius: smallRadius,
                height: 'auto',
                backgroundColor: 'white',
                padding: '10px 25px 10px 25px',
              }}
            >
              {this.state.currentUploads.map((item: IUploadStatus) => {
                return (
                  <ModelSnackBarProgressBarItem
                    uploadManager={this.props.uploadManager}
                    uploadKey={item.key}
                    uploadName={item.name}
                    key={item.key}
                    isSavable={this.props.isSavable}
                    isMediafile={this.props.isMediafile}
                  />
                );
              })}
            </div>
          )}
        </div>
      );
    }

    closeSnackbar() {
      this.state.currentUploads.map((item: IUploadStatus) => {
        if (
          item.status !== EUploadResponseType.PROGRESS &&
          item.status !== EUploadResponseType.ADDED
        ) {
          this.props.uploadManager.deleteDownload(item.key);
        }
      });
      this.setState({
        isClosed: true,
      });
    }
  },
);
